import { FormattedMessage, useIntl } from "react-intl";
import { Images } from "lucide-react";

import { Badge } from "@runcheetah/ui";
import { cn } from "@/lib/utils";
import type {
  ScheduledPostCarouselOutput,
  ScheduledPostCarouselSlideOutput,
} from "@/lib/scheduled-post-preview";

export function CarouselSlideRail({
  carousel,
  selectedSlideId,
  onSelectSlide,
  className,
}: {
  carousel: ScheduledPostCarouselOutput;
  selectedSlideId?: string | null;
  onSelectSlide: (slideId: string) => void;
  className?: string;
}) {
  const intl = useIntl();

  return (
    <div className={cn("min-w-0", className)}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          <FormattedMessage id="carouselReview.slides" defaultMessage="Slides" />
        </p>
        <Badge shape="pill" variant="outline" className="rounded-full px-2.5 py-1 text-[11px]">
          <FormattedMessage
            id="carouselReview.slideCount"
            defaultMessage="{count, plural, one {# slide} other {# slides}}"
            values={{ count: carousel.slides.length }}
          />
        </Badge>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {carousel.slides.map((slideOutput) => {
          const slide = slideOutput.slide;
          const asset = slideOutput.assets[0] ?? null;
          const aspectRatio = getCarouselSlideThumbnailAspectRatio(slideOutput);
          const active = selectedSlideId ? slide.id === selectedSlideId : slide.index === 1;

          return (
            <button
              key={slide.id}
              type="button"
              className={cn(
                "group/slide grid w-[5.75rem] shrink-0 gap-1.5 rounded-xl border bg-background/70 p-1.5 text-left transition-[border-color,box-shadow,background-color]",
                active
                  ? "border-accent/55 bg-accent/[0.075] shadow-[0_0_0_2px_rgba(88,198,142,0.16)]"
                  : "border-border/70 hover:border-foreground/25 hover:bg-background",
              )}
              aria-pressed={active}
              aria-label={intl.formatMessage(
                {
                  id: "carouselReview.selectSlide",
                  defaultMessage: "Select slide {index}: {headline}",
                },
                { index: slide.index, headline: slide.headline },
              )}
              onClick={() => onSelectSlide(slide.id)}
            >
              <span
                className="relative block overflow-hidden rounded-lg bg-muted"
                style={{ aspectRatio }}
              >
                {asset ? (
                  <img
                    src={asset.signedUrl}
                    alt=""
                    className="h-full w-full object-contain"
                    width={asset.width ?? 1080}
                    height={asset.height ?? 1350}
                    loading="lazy"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <Images className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
                <span className="absolute left-1.5 top-1.5 rounded-full bg-background/90 px-1.5 py-0.5 text-[10px] font-bold text-foreground shadow-sm">
                  {slide.index}
                </span>
              </span>
              <span className="truncate text-[11px] font-semibold capitalize text-foreground">
                {slide.role}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function getCarouselSlideThumbnailAspectRatio(slideOutput: {
  assets: Array<{ width?: number | null; height?: number | null }>;
  scenePlan?: { plan: { scene: { canvas: { width: number; height: number } } } } | null;
}): string {
  const canvas = slideOutput.scenePlan?.plan.scene.canvas ?? null;
  if (canvas) {
    return `${canvas.width} / ${canvas.height}`;
  }

  const asset = slideOutput.assets[0] ?? null;
  return `${asset?.width ?? 1080} / ${asset?.height ?? 1350}`;
}

export function CarouselSlideSummary({
  slideOutput,
  compact = false,
  className,
}: {
  slideOutput: ScheduledPostCarouselSlideOutput | null;
  compact?: boolean;
  className?: string;
}) {
  if (!slideOutput) {
    return null;
  }

  const slide = slideOutput.slide;

  return (
    <div
      className={cn(
        "rounded-xl border border-border/70 bg-background/55",
        compact ? "space-y-2 p-3" : "space-y-3 p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <Badge shape="pill" variant="outline" className="rounded-full px-2.5 py-1 text-[11px]">
          <FormattedMessage
            id="carouselReview.slidePosition"
            defaultMessage="Slide {index}"
            values={{ index: slide.index }}
          />
        </Badge>
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {slide.role}
        </span>
      </div>
      <div className="space-y-1.5">
        <p className={cn("font-semibold text-foreground", compact ? "text-sm" : "text-base")}>
          {slide.headline}
        </p>
        {slide.body ? (
          <p className={cn("leading-6 text-muted-foreground", compact ? "text-xs" : "text-sm")}>
            {slide.body}
          </p>
        ) : null}
        {slide.cta ? (
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">
            {slide.cta}
          </p>
        ) : null}
      </div>
    </div>
  );
}
